import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlassCard, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  UserPlus,
  Mail,
  Shield,
  Briefcase,
  Bike,
  Star,
  Zap,
  Clock
} from 'lucide-react';
import { Staff, staffService } from '@/lib/storage';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const positions = ['Junior', 'Mid-Level', 'Senior', 'Lead', 'Manager'];
const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const AdminStaffPage = () => {
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<Staff | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    department: '',
    position: '',
    totalWages: '',
    offDays: [] as string[],
    upiId: '',
    travelsByBike: false,
    bikeRatePerKm: '',
    isSpecial: false,
    entryTime: '09:00',
    exitTime: '18:00',
  });
  const [deptList, setDeptList] = useState(['Engineering', 'Design', 'Marketing', 'Sales', 'Operations', 'HR']);
  const [isCustomDept, setIsCustomDept] = useState(false);
  const [customDeptVal, setCustomDeptVal] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    loadStaff();
    const interval = setInterval(() => loadStaff(true), 10000);
    return () => clearInterval(interval);
  }, []);

  const loadStaff = async (silent = false) => {
    try {
      const data = await staffService.getAll();
      setStaffList(data);
      // Auto-extract unique departments from staff list to keep dropdown populated
      const existingDepts = Array.from(new Set(data.map(s => s.department).filter(Boolean)));
      setDeptList(prev => Array.from(new Set([...prev, ...existingDepts])));
    } catch (error) {
      console.error('Failed to load staff', error);
      if (!silent) {
        toast({ title: 'Failed to load staff', variant: 'destructive' });
      }
    }
  };

  const filteredStaff = staffList.filter(staff =>
    staff.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    staff.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    staff.department.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openCreateDialog = () => {
    setEditingStaff(null);
    setFormData({ name: '', email: '', password: '', department: '', position: '', totalWages: '', offDays: [], upiId: '', travelsByBike: false, bikeRatePerKm: '', isSpecial: false, entryTime: '09:00', exitTime: '18:00' });
    setIsCustomDept(false);
    setCustomDeptVal('');
    setIsDialogOpen(true);
  };

  const openEditDialog = (staff: Staff) => {
    setEditingStaff(staff);
    setFormData({
      name: staff.name,
      email: staff.email,
      password: '',
      department: staff.department,
      position: staff.position,
      totalWages: staff.totalWages?.toString() || '',
      offDays: staff.offDays || [],
      upiId: staff.upiId || '',
      travelsByBike: !!staff.travelsByBike,
      bikeRatePerKm: staff.bikeRatePerKm?.toString() || '',
      isSpecial: !!staff.isSpecial,
      entryTime: staff.entryTime || '09:00',
      exitTime: staff.exitTime || '18:00',
    });
    setIsCustomDept(false);
    setCustomDeptVal('');
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const deptToSave = isCustomDept ? customDeptVal.trim() : formData.department;
    if (!deptToSave) {
      toast({ title: 'Please select or enter a department', variant: 'destructive' });
      return;
    }

    try {
      if (editingStaff) {
        const updates: Partial<Staff> = {
          name: formData.name,
          email: formData.email,
          department: deptToSave,
          position: formData.position,
          totalWages: formData.totalWages ? parseFloat(formData.totalWages) : 0,
          offDays: formData.offDays.length > 0 ? formData.offDays : null,
          isSpecial: formData.isSpecial,
          upiId: formData.upiId || '',
          travelsByBike: formData.travelsByBike,
          bikeRatePerKm: formData.bikeRatePerKm ? parseFloat(formData.bikeRatePerKm) : 0,
          entryTime: formData.entryTime || '09:00',
          exitTime: formData.exitTime || '18:00',
        };
        if (formData.password) {
          updates.password = formData.password;
        }
        await staffService.update(editingStaff.id, updates);
        toast({ title: 'Staff member updated successfully' });
      } else {
        await staffService.create({
          name: formData.name,
          email: formData.email,
          password: formData.password as string,
          department: deptToSave,
          position: formData.position,
          totalWages: formData.totalWages ? parseFloat(formData.totalWages) : 0,
          offDays: formData.offDays.length > 0 ? formData.offDays : null,
          isSpecial: formData.isSpecial,
          upiId: formData.upiId || '',
          travelsByBike: formData.travelsByBike,
          bikeRatePerKm: formData.bikeRatePerKm ? parseFloat(formData.bikeRatePerKm) : 0,
          entryTime: formData.entryTime || '09:00',
          exitTime: formData.exitTime || '18:00',
        });
        toast({ title: 'Staff member added successfully' });
      }

      // Add new department to dropdown list dynamically
      if (isCustomDept && customDeptVal) {
        setDeptList(prev => Array.from(new Set([...prev, customDeptVal.trim()])));
      }
      setIsCustomDept(false);
      setCustomDeptVal('');
      setIsDialogOpen(false);
      loadStaff();
    } catch (error) {
      toast({ title: 'Operation failed', variant: 'destructive' });
    }
  };

  const handleDelete = async (staffId: string) => {
    if (confirm('Are you sure you want to delete this staff member?')) {
      await staffService.delete(staffId);
      toast({ title: 'Staff member deleted' });
      loadStaff();
    }
  };

  // Animation Grid Variants
  const gridVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 10 },
    show: { opacity: 1, scale: 1, y: 0, transition: { type: "spring", stiffness: 350, damping: 25 } }
  };

  return (
    <div className="space-y-6">
      {/* Header section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Staff Management</h2>
          <p className="text-sm text-slate-500">Manage your team members and database credentials.</p>
        </div>
        <Button
          variant="royal"
          onClick={openCreateDialog}
          className="rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg shadow-primary/5 w-full sm:w-auto"
        >
          <UserPlus className="w-4.5 h-4.5" /> Add Staff Member
        </Button>
      </div>

      {/* Search Toolbar */}
      <div className="relative max-w-md w-full">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400" />
        <Input
          placeholder="Search by name, email, or department..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 h-11 rounded-xl bg-white/50 dark:bg-slate-950/40 border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-700 transition-all font-medium text-sm"
        />
      </div>

      {/* Staff Members Grid */}
      <AnimatePresence mode="wait">
        {filteredStaff.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl shadow-sm bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl">
              <CardContent className="py-16 text-center">
                <UserPlus className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-750 mb-4" />
                <h4 className="text-lg font-bold text-slate-850 dark:text-slate-100">No staff members found</h4>
                <p className="text-sm text-slate-500 mb-6 max-w-xs mx-auto">Get started by creating credentials for your first team member.</p>
                <Button variant="royal" onClick={openCreateDialog} className="rounded-xl font-semibold">
                  <Plus className="w-4 h-4 mr-1.5" /> Add Staff Member
                </Button>
              </CardContent>
            </GlassCard>
          </motion.div>
        ) : (
          <motion.div
            key="grid"
            variants={gridVariants}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {filteredStaff.map((staff) => (
              <motion.div
                key={staff.id}
                variants={cardVariants}
                whileHover={{ scale: 1.015 }}
                className="h-full"
              >
                <GlassCard className="h-full border-slate-200/50 dark:border-slate-850 shadow-sm hover:shadow-md transition-all rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl flex flex-col justify-between overflow-hidden">
                  <CardContent className="p-6 flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-3.5 mb-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-emerald-500/10 to-teal-500/10 dark:from-emerald-500/20 dark:to-teal-500/20 border border-emerald-500/25 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                          <span className="font-extrabold text-lg">
                            {staff.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-bold text-base text-slate-850 dark:text-slate-100 truncate">{staff.name}</h3>
                          <p className="text-xs text-slate-450 dark:text-slate-500 font-medium truncate flex items-center gap-1.5 mt-0.5">
                            <Mail className="w-3.5 h-3.5 text-slate-400" />
                            {staff.email}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-350 border-none font-semibold rounded-lg text-[10px] px-2 py-0.5 flex items-center gap-1">
                          <Briefcase className="w-3 h-3 text-slate-400" />
                          {staff.department}
                        </Badge>
                        <Badge className="bg-indigo-500/5 text-indigo-600 dark:text-indigo-400 border border-indigo-200/10 font-bold rounded-lg text-[10px] px-2 py-0.5 flex items-center gap-1">
                          <Shield className="w-3 h-3 text-indigo-400" />
                          {staff.position}
                        </Badge>
                        {staff.totalWages ? (
                          <Badge className="bg-amber-500/5 text-amber-600 dark:text-amber-400 border border-amber-200/10 font-bold rounded-lg text-[10px] px-2 py-0.5 flex items-center gap-1">
                            ₹{staff.totalWages.toLocaleString('en-IN')}/month
                          </Badge>
                        ) : null}
                        {staff.offDays && staff.offDays.length > 0 ? (
                          <Badge className="bg-rose-500/5 text-rose-600 dark:text-rose-400 border border-rose-200/10 font-bold rounded-lg text-[10px] px-2 py-0.5">
                            Off: {staff.offDays.map(d => d.slice(0, 3)).join(', ')}
                          </Badge>
                        ) : null}
                        {staff.upiId ? (
                          <Badge className="bg-emerald-500/5 text-emerald-600 dark:text-emerald-400 border border-emerald-200/10 font-bold rounded-lg text-[10px] px-2 py-0.5">
                            UPI: {staff.upiId}
                          </Badge>
                        ) : (
                          <Badge className="bg-rose-500/5 text-rose-600 dark:text-rose-455 border border-rose-200/10 font-bold rounded-lg text-[10px] px-2 py-0.5">
                            No UPI Configured
                          </Badge>
                        )}
                        {staff.travelsByBike ? (
                          <Badge className="bg-sky-500/5 text-sky-600 dark:text-sky-400 border border-sky-200/10 font-bold rounded-lg text-[10px] px-2 py-0.5 flex items-center gap-1">
                            <Bike className="w-3 h-3 text-sky-400" />
                            Bike: ₹{staff.bikeRatePerKm}/km
                          </Badge>
                        ) : null}
                        {staff.isSpecial ? (
                          <Badge className="bg-purple-500/5 text-purple-600 dark:text-purple-400 border border-purple-200/10 font-bold rounded-lg text-[10px] px-2 py-0.5 flex items-center gap-1">
                            <Star className="w-3 h-3 text-purple-400" />
                            Special
                          </Badge>
                        ) : null}
                        <Badge className="bg-indigo-500/5 text-indigo-600 dark:text-indigo-400 border border-indigo-200/10 font-bold rounded-lg text-[10px] px-2 py-0.5 flex items-center gap-1">
                          <Clock className="w-3 h-3 text-indigo-455" />
                          Shift: {staff.entryTime || '09:00'} - {staff.exitTime || '18:00'}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-4 mt-6 border-t border-slate-100 dark:border-slate-800/40">
                      {staff.isSpecial ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-xl text-xs font-semibold text-purple-600 dark:text-purple-400 hover:text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-950/20"
                          onClick={() => navigate(`/admin/staff/${staff.id}/special`)}
                        >
                          <Zap className="w-3.5 h-3.5 mr-1" />
                          Special Panel
                        </Button>
                      ) : null}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex-1 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-850 hover:bg-slate-100 dark:hover:bg-slate-800"
                        onClick={() => openEditDialog(staff)}
                      >
                        <Pencil className="w-3.5 h-3.5 mr-1.5" />
                        Edit Profile
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex-1 rounded-xl text-xs font-semibold text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/20"
                        onClick={() => handleDelete(staff.id)}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </GlassCard>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-h-[90vh] flex flex-col sm:max-w-md p-0 overflow-hidden rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 dark:border-slate-800/30 shrink-0">
            <DialogTitle className="text-lg font-bold">
              {editingStaff ? 'Edit Staff Member' : 'Add New Staff'}
            </DialogTitle>
            <DialogDescription className="text-sm mt-0.5">
              {editingStaff
                ? 'Update staff information and organizational details'
                : 'Create login credentials for a new team member'
              }
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 scrollbar-none">
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Full Name</label>
                <Input
                  required
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/40"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Email Address</label>
                <Input
                  type="email"
                  required
                  placeholder="john@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/40"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  Password {editingStaff && '(leave empty to keep current)'}
                </label>
                <Input
                  type="password"
                  required={!editingStaff}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-955/40"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Department</label>
                  <Select
                    value={isCustomDept ? "new" : formData.department}
                    onValueChange={(value) => {
                      if (value === "new") {
                        setIsCustomDept(true);
                        setFormData({ ...formData, department: "" });
                      } else {
                        setIsCustomDept(false);
                        setFormData({ ...formData, department: value });
                      }
                    }}
                    required
                  >
                    <SelectTrigger className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-955/40">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {deptList.map((dept) => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                      <SelectItem value="new" className="text-indigo-600 dark:text-indigo-400 font-bold">+ Add New Department</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Position</label>
                  <Select
                    value={formData.position}
                    onValueChange={(value) => setFormData({ ...formData, position: value })}
                    required
                  >
                    <SelectTrigger className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-955/40">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {positions.map((pos) => (
                        <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {isCustomDept && (
                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Custom Department Name</label>
                  <Input
                    required
                    placeholder="e.g. Finance"
                    value={customDeptVal}
                    onChange={(e) => setCustomDeptVal(e.target.value)}
                    className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/40"
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Total Monthly Wages (₹)</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.totalWages}
                  onChange={(e) => setFormData({ ...formData, totalWages: e.target.value })}
                  className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/40"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500">UPI ID for Payouts</label>
                <Input
                  type="text"
                  placeholder="e.g. staffname@upi"
                  value={formData.upiId}
                  onChange={(e) => setFormData({ ...formData, upiId: e.target.value })}
                  className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/40"
                />
              </div>

              {/* Bike Travel Settings */}
              <div className="space-y-3 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800/60">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <label className="text-xs font-bold uppercase tracking-wide text-slate-700 dark:text-slate-350">Travels by Bike</label>
                    <p className="text-[10px] text-slate-400 font-semibold leading-none">Enable daily kilometer tracking and expenses for this employee.</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.travelsByBike}
                    onChange={(e) => setFormData({ ...formData, travelsByBike: e.target.checked })}
                    className="w-4.5 h-4.5 text-indigo-605 border-slate-300 rounded focus:ring-indigo-500 bg-white dark:bg-slate-950"
                  />
                </div>

                {formData.travelsByBike && (
                  <div className="space-y-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
                    <label className="text-[10px] font-extrabold uppercase tracking-wide text-slate-500">Bike Rate Per KM (₹)</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="e.g. 5.50"
                      value={formData.bikeRatePerKm}
                      onChange={(e) => setFormData({ ...formData, bikeRatePerKm: e.target.value })}
                      className="h-9.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950"
                    />
                  </div>
                )}
              </div>
              {/* Shift Hours Settings */}
              <div className="space-y-3 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800/60">
                <div className="space-y-0.5">
                  <label className="text-xs font-bold uppercase tracking-wide text-slate-700 dark:text-slate-350">Shift Timing Settings</label>
                  <p className="text-[10px] text-slate-400 font-semibold leading-none">Define individual start (entry) and end (exit) times for shift.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-extrabold uppercase tracking-wide text-slate-500">Entry Time (Shift Start)</label>
                    <Input
                      type="time"
                      value={formData.entryTime}
                      onChange={(e) => setFormData({ ...formData, entryTime: e.target.value })}
                      className="h-9.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 font-semibold"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-extrabold uppercase tracking-wide text-slate-500">Exit Time (Shift End)</label>
                    <Input
                      type="time"
                      value={formData.exitTime}
                      onChange={(e) => setFormData({ ...formData, exitTime: e.target.value })}
                      className="h-9.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 font-semibold"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Off Days</label>
                <div className="flex flex-wrap gap-2">
                  {DAYS_OF_WEEK.map((day) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => {
                        const newOffDays = formData.offDays.includes(day)
                          ? formData.offDays.filter(d => d !== day)
                          : [...formData.offDays, day];
                        setFormData({ ...formData, offDays: newOffDays });
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                        formData.offDays.includes(day)
                          ? 'bg-rose-500 text-white border-rose-500 shadow-md shadow-rose-500/10'
                          : 'bg-white dark:bg-slate-955/40 text-slate-600 dark:text-slate-400 border-slate-200/60 dark:border-slate-800 hover:border-rose-300 dark:hover:border-rose-700'
                      }`}
                    >
                      {day.slice(0, 3)}
                    </button>
                  ))}
                </div>
                {formData.offDays.length > 0 && (
                  <p className="text-[10px] text-slate-400 font-medium mt-1">
                    {formData.offDays.length} day(s) off per week
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl bg-purple-50 dark:bg-purple-950/20 border border-purple-200/50 dark:border-purple-800/30">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  <span className="text-sm font-bold text-purple-700 dark:text-purple-300">Special Staff</span>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, isSpecial: !formData.isSpecial })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.isSpecial ? 'bg-purple-600' : 'bg-slate-300 dark:bg-slate-700'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.isSpecial ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>

            <DialogFooter className="bg-slate-50 dark:bg-slate-955/40 px-6 py-4 border-t border-slate-100 dark:border-slate-800/30 flex flex-col sm:flex-row gap-2 shrink-0">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-xl">
                Cancel
              </Button>
              <Button type="submit" variant="royal" className="rounded-xl">
                {editingStaff ? 'Update' : 'Create'} Staff
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminStaffPage;
